import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Configurator } from '../../../catalog/products/interface/configurable';
import { Product } from '../../../catalog/products/product';
import { CatalogBase } from '../../catalog/products/catalog';
import { ConfiguratorType, ProductSerializedBase } from '../../catalog/products/interface/configurable';
import { JsonCatalog } from '../../catalog/products/interface/context';
import { ProductContextBase } from '../../context/child/product-context.service';
import { ContextServiceBase } from '../../context/context.service';
import { RulesFactoryBase } from './rules/rules-factory.factory';
import { RulesBase } from './rules/rules.class';
import { SchemeSerializedBase } from './scheme.interface.js';

export interface AddResult {
    status: boolean;
    productsConflict: Product[];
}

export class SchemeBase {
    public products: Product[] = [];
    public uniqueId: string;
    public configurators: Map<string, Configurator> = new Map<string, Configurator>();
    public rules: RulesBase;

    public get porta(): Configurator {
        return this.configurators.get(ConfiguratorType.PORTA);
    }

    public get customer(): Configurator {
        return this.configurators.get(ConfiguratorType.CONVERGENCE);
    }

    public get locales(): Configurator {
        return this.configurators.get(ConfiguratorType.LOCALES);
    }

    /**
     * Unserialize SchemeBase :
     * - Context
     * - Products with uniqueID
     * @param data
     * @param contextService
     * @param products
     * @returns
     */
    public static unserialize(
        data: SchemeSerializedBase,
        contextService: ContextServiceBase,
        products: JsonCatalog,
    ): Observable<SchemeBase> {
        return ProductContextBase.unserialize(data.productContext, contextService).pipe(
            map((productContext) => {
                const intance: SchemeBase = new SchemeBase(productContext, data.rulesType, data.uniqueId);
                // @todo : A voir si le context n'est pas détruit par le add en pratique
                for (const key in data.products) {
                    const p = CatalogBase.getInstance(
                        products[data.products[key].gencode],
                        productContext,
                        data.products[key].uniqueId,
                    );

                    if (!intance.add(p)) {
                        alert('unserialize error');
                    }
                }
                return intance;
            }),
        );
    }

    constructor(
        public productContext: ProductContextBase,
        rules?: string,
        uniqueId?: string,
    ) {
        productContext.scheme = this;
        this.uniqueId = !uniqueId ? Date.now() + '-' + Math.floor(Math.random() * 1000) : uniqueId;
        if (rules) {
            this.rules = RulesFactoryBase.getNewInstanceOf(rules, this);
        } else {
            this.rules = new RulesBase(this);
        }
    }

    /**
     * Remove products
     * @todo :Reset RulesBase ?
     * @returns
     */
    public clear(): SchemeBase {
        this.products = [];
        return this;
    }

    /**
     * Unserialize scheme from Params
     * @returns
     */
    public serialize(redirect: string = null): SchemeSerializedBase {
        const sereialized: ProductSerializedBase[] = [];
        for (const key in this.products) {
            sereialized.push(this.products[key].serialize());
        }
        return {
            productContext: this.productContext.serialize(),
            rulesType: this.rules.KEY,
            products: sereialized,
            uniqueId: this.uniqueId,
            redirect,
        };
    }

    /**
     *
     * Add product in scheme :
     * 1  - Check rules for add product
     * 2a - Remove product returned by rulesConflict
     * 3a - Add Recusif
     * 2b - Add product*qty
     * 3b - call function updateSchemeOnAdd of product (Exp : Plan update context)
     * 4  - Define rules if is first Iteme.
     * 5  - return boolean
     *
     * PS :  we don't reserve stock in scheme add beacause this code is angular less and we need to create scheme out
     * of cart exemple for View.component.ts
     * @param product
     * @param qty
     * @param force
     * @returns
     */
    public add(product: Product, qty: number = 1, force: boolean = false): AddResult {
        const productsConflict: Product[] = this.rules.getConflictProject(this, product);
        const addResult: AddResult = {
            status: false,
            productsConflict,
        };
        if (productsConflict.length > 0) {
            if (force) {
                for (const key in productsConflict) {
                    if (!this.remove(productsConflict[key].uniqueId)) {
                        return addResult;
                    }
                }
                const tmpAddResult: AddResult = this.add(product, qty, force);
                return {
                    status: true,
                    productsConflict: productsConflict.concat(tmpAddResult.productsConflict),
                };
            } else {
                console.log('Conflict product', productsConflict);
                return addResult;
            }
        } else {
            for (let i = 0; i < qty; i++) {
                this.products.push(product);
            }
            product.updateSchemeOnAdd(this);
            if (this.products.length - product.qty === 0) {
                this.rules = RulesFactoryBase.getRulesFor(this);
            }
            addResult.status = true;
            return addResult;
        }
    }

    /**
     * addConfigurator
     * @param configurator
     * @param product
     */
    public addConfigurator(configurator: Configurator, product?: Product): void {
        if (configurator.value) {
            this.configurators.set(configurator.id, configurator);
            if (product) {
                this.add(product);
            }
        } else {
            this.configurators.delete(configurator.id);
            if (product) {
                this.remove(product.uniqueId);
            }
        }
    }

    /**
     * addConfigurators
     * This allows product context update once instead of update on each configurator addition
     * @param configurator
     * @param product
     */
    public addConfigurators(configurators: Configurator[], product?: Product): void {
        if (!configurators || !configurators.length) {
            return;
        }
        for (let configurator of configurators) {
            this.addConfigurator(configurator, product);
        }
        this.updateContext(this.productContext);
    }

    /**
     * Set new context in all product of scheme
     * @param productContext
     */
    public updateContext(productContext: ProductContextBase): void {
        for (const key in this.products) {
            if (this.products.hasOwnProperty(key)) {
                this.products[key].context = productContext;
            }
        }
        this.productContext = productContext;
    }

    /**
     * Remove product and call func updateSchemeOnRemove
     * (Exp : if you remove phone, updateSchemeOnRemove remove EDP if you have one in your scheme)
     * @param uniqueId
     */
    public remove(uniqueId: string): boolean {
        const product: Product = this.products.find((obj) => obj.uniqueId === uniqueId);
        if (!product) {
            return false;
        }
        const newProduct: Product = product;
        this.products = this.products.filter((obj) => obj.uniqueId !== uniqueId);
        newProduct.updateSchemeOnRemove(this);
        return true;
    }

    /**
     * Remove product by gencode and call func updateSchemeOnRemove
     * @param gencode
     */
    public removeGenCode(gencode: string): boolean {
        const product: Product = this.products.find((obj) => obj.gencode === gencode);
        if (!product) {
            return false;
        }
        const newProduct: Product = product;
        this.products = this.products.filter((obj) => obj.gencode !== gencode);
        newProduct.updateSchemeOnRemove(this);
        return true;
    }

    /**
     * Return product by uniqueID
     * @param gencode
     * @returns
     */
    public getProductUniqueId(gencode: string): string {
        const product = this.products.find((obj) => obj.gencode === gencode);
        return product ? product.uniqueId : null;
    }

    /**
     * Check if you have product with gencode set in params
     * @param gencode
     * @returns
     */
    public hasProduct(gencode: string): boolean {
        return this.products.some((obj) => obj.gencode === gencode);
    }

    /**
     * Return first product of type set in param of this function
     * @param typeProduct
     * @returns
     */
    public getProductByType(typeProduct: any): Product {
        return this.products.find((obj) => obj instanceof typeProduct);
    }

    /**
     * Return products of type set in param of this function
     * @param typeProduct
     * @returns
     */
    public getProductsByType(typeProduct: any): Product[] {
        return this.products.filter((obj) => obj instanceof typeProduct);
    }
}
